import {
  DataGridPro,
  GridColumnHeaderParams,
  GridColumns,
  GridRenderCellParams,
  GridValueSetterParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { newMultiElementDataGridSxGet } from '../../modules/tools/utils/tools.utils';
import SwipeBlocker from '../tool/components/SwipeBlocker';
import { contactsWithExtraColumns } from './contacts.mock';
import { useStyles } from '../../new_shared/utils/shared.utils';
import {
  ColumnNameProps,
  CommunityToolInformationParams,
} from '../../modules/communities/models/communities.types';
import ColumnName from './ColumnName';
import { useCallback, useEffect, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import ColumnActions from './ColumnActions';
import ColumnHeader from './ColumnHeader';
import { NormalCell } from '../tool/components/ToolMultiElementTableCells';

import { IconTextButton } from '../../shared/ui/IconTextButton/IconTextButton';
import { IconButton } from '../../shared/ui/IconButton/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputFlat } from '../../shared/ui/InputFlat/InputFlat';
import { useDataGrid } from '../../shared/hooks/use-data-grid.hook';
import { useShared } from '../../shared/hooks/use-shared.hook';
import ColumnTextArea from '../../modules/tools/components/ToolMultiElementTable/ColumnTextArea';
import { ColumnWithBackground } from './ColumnWithBackground';
import { useContactsStore } from '../../modules/contacts/stores/use-contacts.store';
// import { shallow } from 'zustand/shallow';

const Toolbar = ({ showOnlySearch }: { showOnlySearch?: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  // const classes = useToolbarStyles();
  const apiRef = useGridApiContext();

  const handleChange = (value: string) => {
    apiRef.current.setQuickFilterValues([value]);
  };

  const toggleFilterPanel = () => {
    const isOpen = (apiRef.current.state as any).filter?.panel?.open;

    if (isOpen) {
      apiRef.current.hideFilterPanel();
    } else {
      apiRef.current.showFilterPanel();
    }
  };

  return (
    <Box>
      {showOnlySearch ? (
        <Box className="py-2 flex w-1/3">
          <InputFlat
            className="flex flex-row items-center"
            startAdornmentElement={
              <FontAwesomeIcon
                className="mx-1 p-2 pr-0"
                style={{ color: theme.palette.text.secondary }}
                icon={['fas', 'search']}
              />
            }
            padding="1rem 0.25rem"
            placeholder={t('app.search')}
            onChange={handleChange}
          />
        </Box>
      ) : (
        <Box className="py-2 flex flex-col w-full gap-1">
          <Box
            className="flex flex-row justify-between border-solid border-0 border-t border-b py-2"
            sx={{ borderColor: 'border.app' }}
          >
            <Box className="flex flex-row gap-2">
              <IconTextButton
                // disabled={props.disabled}
                icon={['fal', 'plus']}
                padding={'0.25rem 0.5rem'}
                preset="primary"
                onClick={() => {}}
              >
                {t('contacts.toolbar.group.add_person')}
              </IconTextButton>
              <IconTextButton
                // disabled={props.disabled}
                icon={['fal', 'plus']}
                padding={'0.25rem 0.5rem'}
                preset="primary"
                onClick={() => {}}
              >
                {t('contacts.toolbar.group.invite_person')}
              </IconTextButton>
            </Box>
            <Box
              className="flex flex-row items-center"
              sx={{ color: 'text.secondary' }}
            >
              <InputFlat
                className="flex flex-row items-center mx-1"
                startAdornmentElement={
                  <FontAwesomeIcon
                    className="mx-1 p-2 pr-0"
                    style={{ color: theme.palette.text.secondary }}
                    icon={['fas', 'search']}
                  />
                }
                padding="0.5rem 0.25rem"
                placeholder={t('app.search')}
                onChange={handleChange}
              />
              <IconButton
                classes="mx-1 text-base"
                icon={['far', 'filter']}
                preset="tool-icon"
                iconSize="inherit"
                padding="0.4rem"
                onClick={toggleFilterPanel}
              />
              <IconButton
                classes="mx-1 text-base"
                icon={['fas', 'arrow-down-to-line']}
                preset="tool-icon"
                iconSize="inherit"
                padding="0.4rem"
              />
            </Box>
          </Box>
          <Box className="self-end">
            Hinweis: Honzontales Scrollen (Shift + Mausrad)
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Contacts = ({
  hideAction,
  showOnlySearch,
}: {
  hideAction?: boolean;
  showOnlySearch?: boolean;
}) => {
  const { t } = useTranslation();
  const { localeTextGet } = useDataGrid();
  const { fullNameGet } = useShared();
  const contacts = useContactsStore((state) => state.contacts);
  const dataLoading = useContactsStore((state) => state.dataLoading);
  const apiRef = useGridApiRef();
  const classes = useStyles();

  const defaultRenderHeader = useCallback(
    (params: GridColumnHeaderParams) => (
      <ColumnHeader {...params} title={params.field} onToggle={() => {}} />
    ),
    []
  );
  const baseColumns: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('app.name').toUpperCase(),
        width: 240,
        valueGetter: (params: GridValueSetterParams) => {
          return fullNameGet(
            params.row?.first_name,
            params.row?.last_name,
            params.row?.nickname
          );
        },
        renderCell: (params: GridRenderCellParams<ColumnNameProps>) => (
          <ColumnName {...params} />
        ),
      },
      {
        field: 'email',
        headerName: t('app.emailAddress').toUpperCase(),
        width: 280,
        renderCell: (params: GridRenderCellParams) => (
          <NormalCell params={params} />
        ),
      },
      {
        field: 'birthday',
        headerName: 'birthday'.toUpperCase(),
        width: 120,
        headerClassName: 'custom-gray-header',
        renderCell: (params: GridRenderCellParams) => (
          <ColumnWithBackground params={params} />
        ),
      },
      {
        field: 'language',
        headerName: 'language'.toUpperCase(),
        width: 120,
        headerClassName: 'custom-gray-header',
        renderCell: (params: GridRenderCellParams) => (
          <ColumnWithBackground params={params} />
        ),
      },
      {
        // flex: 1,
        field: 'actions',
        headerName: 'Aktionen',
        minWidth: 120,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          return <ColumnActions row={params.row} />;
        },
      },
    ],
    []
  );

  const columns: GridColumns = useMemo(() => {
    const filteredBase = hideAction
      ? baseColumns.filter((col) => col.field !== 'actions')
      : baseColumns;

    return filteredBase.map((col) => ({
      ...col,
      renderHeader: defaultRenderHeader,
      sortingOrder: ['asc', 'desc'],
    }));
  }, [hideAction]);

  useEffect(() => {
    const extraHeaders = contactsWithExtraColumns.extraHeaders;

    if (extraHeaders && extraHeaders.length > 0) {
      extraHeaders.map((extratitle: string, index: number) => {
        columns.push({
          field: extratitle,
          headerName: extratitle.toUpperCase(),
          valueGetter: (params: GridValueSetterParams) => {
            return params.row.tool_informations
              ? params.row.tool_informations[index] !== 'undefined'
                ? params.row.tool_informations[index]?.information
                : ''
              : '';
          },
          renderCell: (params: GridRenderCellParams) => {
            const extendedParams: CommunityToolInformationParams = {
              ...params,
              information: params.row.tool_informations
                ? params.row.tool_informations[index] !== 'undefined'
                  ? params.row.tool_informations[index]?.information
                  : ''
                : '',
            };

            return <ColumnTextArea {...extendedParams} />;
          },
          width: 220,
          sortingOrder: ['asc', 'desc'],
        });
      });
    }
  }, [contactsWithExtraColumns.extraHeaders]);

  // add classname for a new row containing the total
  const getRowClassName = (params: any) => {
    return params.indexRelativeToCurrentPage % 2 === 0
      ? classes.evenRow
      : classes.oddRow;
  };

  const panelProps = useMemo(
    () => ({
      sx: {
        right: '80px !important',
        left: 'auto !important',
      },
    }),
    []
  );
  const toolbarProps = useMemo(
    () => ({
      showOnlySearch: showOnlySearch,
    }),
    []
  );
  return (
    <Box
      className="py-4 px-8"
      sx={{
        maxWidth: 1200,
        '& .MuiDataGrid-root .MuiDataGrid-autoHeight': {
          border: 'none',
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: 'none',
        },
        '& .MuiDataGrid-iconButtonContainer': {
          visibility: 'visible',
          width: 'auto',
          backgroundColor: 'inherit',
        },
      }}
    >
      <SwipeBlocker>
        <DataGridPro
          apiRef={apiRef}
          autoHeight
          sx={{
            ...newMultiElementDataGridSxGet(),
            border: 'none',
            '& .MuiDataGrid-main': {
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
            },
            '& .custom-gray-header': {
              backgroundColor: '#EEF0F2',
            },
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon':
              {
                opacity: 1,
                color: '#C9CDD0',
              },
          }}
          columns={columns}
          components={{
            Toolbar: Toolbar,
            ColumnSortedAscendingIcon: ArrowDropUpIcon,
            ColumnSortedDescendingIcon: ArrowDropDownIcon,
          }}
          componentsProps={{
            panel: panelProps,
            toolbar: toolbarProps,
          }}
          disableColumnMenu
          experimentalFeatures={{ newEditingApi: true }}
          columnBuffer={15}
          rowBuffer={10}
          loading={dataLoading}
          localeText={localeTextGet()}
          rows={contacts}
          getRowId={(row) => row.id}
          getRowClassName={getRowClassName}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          pageSize={25}
          pagination={true}
        />
      </SwipeBlocker>
    </Box>
  );
};

export default Contacts;
